import { createContext, useContext, useState, PropsWithChildren, useEffect } from "react";
import { AuthUseState } from "../hooks/auth/use_use_cases";
import { ComercialState } from "../hooks/comercial/use_use_cases";
import { CommonsState } from "../hooks/commons/use_use_cases";
import { ObrasState } from "../hooks/obras/use_use_cases";
import { RhState } from "../hooks/rh/use_use_cases";
import { ReportsState, useReportsUseCases } from "../hooks/reports/use_use_cases";
import { EquipamentoMedicaoState, useEquipamentoMedicaoUseCases } from "../hooks/equipamentoMedicoes/use_use_cases";
import { FinanceiroState, useFinanceiroUseCases } from "../hooks/financeiro/use_use_cases";
import { AuditorState, useAuditorUseCases } from "../hooks/auditor/use_use_cases";
import { ComprasState, useComprasUseCases } from "../hooks/compras/use_use_cases";
import { SnackbarProvider } from "notistack";
import { NotificationPanel } from "../../common/contexts/page_content_context/components/notification_panel";
import { QualidadeState, useQualidadeUseCases } from "../hooks/qualidade/use_use_cases";
import { ProjetosState, useProjetosUseCases } from "../hooks/projetos/use_use_cases";
import { DiarioObraState, useDiarioObraUseCases } from "../hooks/diario_obra/use_use_cases";
import { ControlePatrimonioState, useControlePatrimonioUseCases } from "../hooks/controledePatrimonio/use_use_cases";
import { TerceirizacaoState, useTerceirizacaoUseCases } from "../hooks/terceirizacao/use_use_cases";
import { EntregaObrasState, useEntregaObrasUseCases } from "../hooks/entregaObras/use_use_cases";
import { AdministracaoState, useAdministracaoUseCases } from "../hooks/administracao/use_use_cases";
import { ControleDocumentosState } from "../hooks/controleDocumentos/use_use_cases";
import { ControleEstoqueState, useControleEstoqueUseCases } from "../hooks/controleEstoque/use_use_cases";
import { GestaoEPIState } from "../hooks/gestaoEpi/use_use_cases";
import { IncorporacaoState, useIncorporacaoUseCases } from "../hooks/incorporacao/use_use_cases";

import {
  useCommonsUseCases,
  useAuthUseCases,
  useComercialUseCases,
  useRhUseCases,
  useObrasUseCases,
  useControleDocumentosUseCases,
  useGestaoEPIUseCases,
} from "../hooks";

/**
 * Context State
 */

type DIState = {
  commons: CommonsState;
  auth: AuthUseState;
  administracao: AdministracaoState;
  auditor: AuditorState;
  qualidade: QualidadeState;
  comercial: ComercialState;
  incorporacao: IncorporacaoState;
  financeiro: FinanceiroState;
  rh: RhState;
  obras: ObrasState;
  reports: ReportsState;
  equipamentos: EquipamentoMedicaoState;
  compras: ComprasState;
  projetos: ProjetosState;
  diarioObra: DiarioObraState;
  controlePatrimonio: ControlePatrimonioState;
  terceirizacao: TerceirizacaoState;
  entregaObras: EntregaObrasState;
  controleDocumentos: ControleDocumentosState;
  controleEstoque: ControleEstoqueState;
  equipamentoMedicao: EquipamentoMedicaoState;
  gestaoEpi: GestaoEPIState;
};

const DIInitialState: DIState = {
  commons: useCommonsUseCases.getState(),
  auth: useAuthUseCases.getState(),
  administracao: useAdministracaoUseCases.getState(),
  auditor: useAuditorUseCases.getState(),
  qualidade: useQualidadeUseCases.getState(),
  comercial: useComercialUseCases.getState(),
  incorporacao: useIncorporacaoUseCases.getState(),
  financeiro: useFinanceiroUseCases.getState(),
  rh: useRhUseCases.getState(),
  obras: useObrasUseCases.getState(),
  reports: useReportsUseCases.getState(),
  equipamentos: useEquipamentoMedicaoUseCases.getState(),
  compras: useComprasUseCases.getState(),
  projetos: useProjetosUseCases.getState(),
  diarioObra: useDiarioObraUseCases.getState(),
  controlePatrimonio: useControlePatrimonioUseCases.getState(),
  terceirizacao: useTerceirizacaoUseCases.getState(),
  entregaObras: useEntregaObrasUseCases.getState(),
  controleDocumentos: useControleDocumentosUseCases.getState(),
  controleEstoque: useControleEstoqueUseCases.getState(),
  equipamentoMedicao: useEquipamentoMedicaoUseCases.getState(),
  gestaoEpi: useGestaoEPIUseCases.getState(),
};

/**
 * Context
 */

export const DIContext = createContext<DIState>(DIInitialState);

/**
 * Context provider
 */

export const DIContextProvider = ({ children }: PropsWithChildren) => {
  const [contextState, setContextState] = useState<DIState>(DIInitialState);

  useEffect(() => {
    setContextState(DIInitialState);
  }, []);

  return (
    <DIContext.Provider value={contextState}>
      <SnackbarProvider
        maxSnack={10}
        autoHideDuration={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ containerRoot: "mt-[7.5rem] z-[10000]" }}
        Components={{
          success: NotificationPanel,
          info: NotificationPanel,
          warning: NotificationPanel,
          error: NotificationPanel,
        }}>
        {children}
      </SnackbarProvider>
    </DIContext.Provider>
  );
};

/**
 * useDependencyInjection
 */

export const useDependencyInjection = (): DIState => {
  const context = useContext(DIContext);
  return context;
};
